import { IIIFBuilder } from '@iiif/builder';
import { collectionStore } from '@store/collections.svelte';
import type { ManifestNormalized } from '@iiif/presentation-3-normalized';
import type { RFCanvasMeta, RFCanvasLegend } from '@src/types/reframe';

type ManifestInstanceBuilder = Parameters<typeof IIIFBuilder.prototype.createManifest>[1] extends (arg: infer T) => void ? T : never;
type CanvasInstanceBuilder = Parameters<ManifestInstanceBuilder['createCanvas']>[1] extends (arg: infer T) => void ? T : never;

const getImageMimeTypeFromSrc = (src: string): string => {
	const extension = src.split('.').pop()?.split('?')[0].toLowerCase();
	const mimeTypes: Record<string, string> = {
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		webp: 'image/webp',
		gif: 'image/gif',
		svg: 'image/svg+xml',
		bmp: 'image/bmp',
		ico: 'image/x-icon',
		avif: 'image/avif',
	};

	return extension && mimeTypes[extension] ? mimeTypes[extension] : 'application/octet-stream';
};

const setCanvasMetas = (canvasBuilder: CanvasInstanceBuilder, legend: RFCanvasLegend, lang: string) => {
	const meta = [];
	// based on legend meta
	for (const key of Object.keys(legend) as (keyof RFCanvasLegend)[]) {
		const element = legend[key];
		if (typeof element !== 'undefined') {
			meta.push({ label: { [lang]: ['legend-' + key] }, value: { [lang]: [element] } });
		}
	}

	canvasBuilder.setMetadata(meta);
};

const createCanvas = (
	manifestBuilder: ManifestInstanceBuilder,
	{ currentCollectionCanvas, lang = '' }: { currentCollectionCanvas?: RFCanvasMeta; lang?: string } = {}
) => {
	if (!currentCollectionCanvas) return;

	manifestBuilder.createCanvas(currentCollectionCanvas.canvasId || `/canvas/${currentCollectionCanvas.uuid}`, (canvasBuilder) => {
		let canvasVaultData = null;
		if (currentCollectionCanvas.canvasId) canvasVaultData = IIIF_VAULT.get(currentCollectionCanvas.canvasId);
		if (canvasVaultData) {
			const annoPage = IIIF_VAULT.get(canvasVaultData.items[0]);
			if (!annoPage || !annoPage.items?.length) return;

			const anno = IIIF_VAULT.get(annoPage.items[0]);
			if (!anno || !anno.body?.length) return;

			const body = IIIF_VAULT.get(anno.body[0]);
			if (!body || !body.type) return;

			canvasBuilder.setWidth(body.width);
			canvasBuilder.setHeight(body.height);
			if (currentCollectionCanvas.legend) setCanvasMetas(canvasBuilder, currentCollectionCanvas.legend, lang);
			canvasBuilder.createAnnotation(anno.id, { ...anno, body: body });
		} else {
			canvasBuilder.setWidth(currentCollectionCanvas.getCanvasSize().width);
			canvasBuilder.setHeight(currentCollectionCanvas.getCanvasSize().height);
			if (currentCollectionCanvas.legend) setCanvasMetas(canvasBuilder, currentCollectionCanvas.legend, lang);
			canvasBuilder.createAnnotation(`/annotation/${currentCollectionCanvas.uuid}`, {
				id: `/annotation/${currentCollectionCanvas.uuid}`,
				type: 'Annotation',
				motivation: 'painting',
				body: {
					id: `/${currentCollectionCanvas.src}`,
					type: 'Image',
					format: getImageMimeTypeFromSrc(currentCollectionCanvas.src),
					height: currentCollectionCanvas.getCanvasSize().height,
					width: currentCollectionCanvas.getCanvasSize().width,
				},
			});
		}
	});
};
export const createFullCollection = (lang: string = 'en'): { collection: unknown; manifests: unknown[] } => {
	const builder = new IIIFBuilder();

	const manifests: { manifest: ManifestNormalized; presentation2: unknown; presentation3: unknown }[] = [];

	collectionStore.collections.items.forEach((collection) => {
		const manifest = createManifestFromUUID(collection.uuid, lang, builder);
		if (!manifest) return;
		manifests.push(manifest);
	});

	const collection = builder.createCollection('reframe', () => {});

	collection.items = manifests.map((manifest) => manifest.manifest);

	return {
		collection: builder.toPresentation3({ id: collection.id, type: 'Collection' }),
		manifests: manifests.map((manifest) => manifest.presentation3),
	};
};

export const createManifestFromUUID = (collectionUUID = '', lang: string = 'en', builder = new IIIFBuilder()) => {
	if (!collectionUUID) return; // add better return type
	const currentCollection = collectionStore.collections.items.find((collection) => collection.uuid === collectionUUID);
	if (!currentCollection) return; // add better return type

	const manifest = builder.createManifest(`${collectionUUID}`, (manifestBuilder) => {
		currentCollection.meta.titles.forEach((title) => {
			manifestBuilder.addLabel(title.content, lang);
		});
		currentCollection.meta.descriptions.forEach((description) => {
			manifestBuilder.addSummary(description.content, lang);
		});
		currentCollection.canvases.forEach((collectionCanvas) => {
			createCanvas(manifestBuilder, { currentCollectionCanvas: collectionCanvas, lang });
		});
	});

	return {
		manifest: manifest,
		presentation2: builder.toPresentation2({ id: manifest.id, type: 'Manifest' }),
		presentation3: builder.toPresentation3({ id: manifest.id, type: 'Manifest' }),
	};
};
